import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationDialogComponent } from '@app/sharedModule/components/confirmation-dialog/confirmation-dialog.component';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { Shippings } from '@pages/checkout-app/types';
import { CHECKOUT_URL } from '@pages/checkout-app/utils';
import { CheckoutSettings, CurrencyUnit } from '@pdqdev/pdq-shared/dist/entities';
import { ABTestVariantType, CheckoutTestGroups } from '@pdqdev/pdq-shared/dist/interfaces';
import { ShopService } from '@services/shops/shop.service';
import { ToastService } from '@storybook/services/toast/toast.service';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AbtestDialogComponent } from './dialogs/abtest-dialog/abtest-dialog.component';
import { ABTest_Columns, ACTIONS, ColumnTypes } from './models';

export const Options = [
  {
    label: 'View',
    value: ACTIONS.EDIT,
  },
  {
    label: 'Duplicate',
    value: ACTIONS.DUPLICATE,
  },
  {
    label: 'Stop test',
    value: ACTIONS.STOP,
  },
];

@Component({
  selector: 'abtest',
  templateUrl: './abtest.component.html',
  styleUrls: ['./abtest.component.scss'],
  standalone: false,
})
export class AbtestComponent implements OnInit, OnDestroy {
  destroy$ = new Subject();
  // store
  activeCheckout$: Observable<Shippings> = this.store.select(fromCheckoutReducer.selectActiveCheckout);
  variantTestGroups$: Observable<CheckoutTestGroups> = this.store.select(fromCheckoutReducer.selectVariantTestGroups);
  isLoading$: Observable<boolean> = this.store.select(fromCheckoutReducer.selectIsABTestLoading);
  settings$: Observable<CheckoutSettings> = this.store.select(fromCheckoutReducer.selectSettings);

  // configurations
  readonly columns = [...ABTest_Columns];
  readonly ColumnTypes = ColumnTypes;
  readonly options = [...Options];
  dialogRef: MatDialogRef<any>;

  originalVariantId: string;
  originalMethod;
  currency: CurrencyUnit;
  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private store: Store,
    private actions: Actions,
    private toastService: ToastService,
    private router: Router,
    private shopService: ShopService,
  ) {}

  ngOnInit(): void {
    this.loadABTestsGroups();
    this.detectSuccess();
    this.detectFailure();
    this.initCurrency();
  }

  ngOnDestroy() {
    this.store.dispatch(CheckoutActions.resetActiveCheckout());
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  initCurrency() {
    this.currency = this.shopService.shopCurrency;
  }

  detectSuccess() {
    this.actions
      .pipe(
        takeUntil(this.destroy$),
        ofType(
          CheckoutActions.saveVariantTestSuccess,
          CheckoutActions.editVariantTestSuccess,
          CheckoutActions.setVariantTestWinnerSuccess,
          CheckoutActions.stopVariantTestSuccess,
          CheckoutActions.deleteVariantTestSuccess,
        ),
      )
      .subscribe((result) => {
        this.store.dispatch(CheckoutActions.loadVariantTestGroups({ originalVariantId: this.originalVariantId }));
        if (result.type?.includes('winner')) {
          // refresh current original checkout details on winner action
          this.store.dispatch(CheckoutActions.loadCheckoutById({ checkoutId: this.originalVariantId }));
        }
        this.store.dispatch(CheckoutActions.loadCheckouts());
        this.toastService.showToast({
          message: result.type,
          mode: 'info',
        });
        this.dialogRef?.close();
      });
  }

  detectFailure() {
    this.actions
      .pipe(
        takeUntil(this.destroy$),
        ofType(
          CheckoutActions.saveVariantTestFailure,
          CheckoutActions.editVariantTestFailure,
          CheckoutActions.deleteVariantTestFailure,
          CheckoutActions.stopVariantTestFailure,
          CheckoutActions.setVariantTestWinnerFailure,
        ),
      )
      .subscribe((result) => {
        this.toastService.showToast({
          message: result.type,
          mode: 'danger',
        });
      });
  }

  loadABTestsGroups() {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((queryParam: any) => {
      this.originalVariantId = queryParam.id;
      this.store.dispatch(CheckoutActions.loadCheckoutById({ checkoutId: this.originalVariantId }));
      this.store.dispatch(CheckoutActions.loadVariantTestGroups({ originalVariantId: this.originalVariantId }));
    });
  }

  createABTest() {
    let isRunningABTestsExists: boolean = false;
    this.variantTestGroups$.pipe(take(1)).subscribe((abTest) => {
      isRunningABTestsExists = abTest.testGroups.some((group) => !group.data.finishedAt);
      if (isRunningABTestsExists) {
        this.toastService.showToast({
          message: 'Cannot open new ab test while some is already running',
          mode: 'danger',
        });
        return;
      }
      this.openDialog({ data: { mode: ACTIONS.CREATE } });
    });
  }

  openDialog(data: any) {
    this.dialogRef = this.dialog.open(AbtestDialogComponent, {
      ...data,
    });
  }

  onAction(event: ACTIONS, testGroup: any) {
    switch (event) {
      case ACTIONS.DUPLICATE: {
        this.openDialog({ data: { mode: ACTIONS.DUPLICATE, testVariant: testGroup?.data } });
        break;
      }
      case ACTIONS.EDIT: {
        this.openDialog({ data: { mode: ACTIONS.EDIT, testVariant: testGroup?.data } });
        break;
      }
      case ACTIONS.STOP: {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
          data: {
            title: 'Stop AB test',
            description: 'Are you sure you want to stop this AB test?',
          },
          disableClose: true,
        });
        dialogRef.afterClosed().subscribe((value) => {
          value ? this.store.dispatch(CheckoutActions.stopVariantTest({ variantTestId: +testGroup?.data?.id })) : null;
        });
        break;
      }
      case ACTIONS.DELETE: {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
          data: {
            title: 'Delete AB test',
            description: 'Are you sure you want to delete this AB test?',
          },
          disableClose: true,
        });
        dialogRef.afterClosed().subscribe((value) => {
          value
            ? this.store.dispatch(CheckoutActions.deleteVariantTest({ variantTestId: +testGroup?.data?.id }))
            : null;
        });
        break;
      }
    }
  }

  setWinner(testGroup, type: ABTestVariantType) {
    const variantTestId = +testGroup?.data?.id;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Set this variant as a winner?',
        description: 'This action will change the current method settings according to the winner variant.',
      },
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((value) => {
      value ? this.store.dispatch(CheckoutActions.setVariantTestWinner({ variantTestId, variantType: type })) : null;
    });
  }

  backToList() {
    this.router.navigate([`${CHECKOUT_URL}`]);
  }
}
